import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, select, put, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import { IViewer, loadingState, updateViewerState, ViewerState } from 'core/slices/player';
import { EntityTypes } from 'constants/entities';
import { updateState } from 'core/actions/product/state';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import { RootState } from 'types';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { IRegisterAction, registerAction } from 'core/slices/register';
import { selectActionCurrent } from 'core/selectors/product/action/actionCurrent';
import { LoadingState } from 'types/domain/base';
import { loadSceneAsset } from 'core/sagas/product/assets/load';
import { selectScene } from 'core/selectors/product/scene/scene';
import { ISceneDAO, SceneType } from 'types/domain/scene';

// How many scenes ahead of the current one to prefetch (1 = immediate next only).
const LOOKAHEAD = 3;

function* viewerStateSaga(action: PayloadAction<IViewer>) {
  //const prodEnts = yield select((state: RootState) => state.entity.present);
  if (action.payload.state === ViewerState.RESTING) {
    yield delay(100);
    yield loadNextScene();
  }
}

function* registerActionSaga(action: PayloadAction<Partial<IRegisterAction>>) {
  //const prodEnts = yield select((state: RootState) => state.entity.present);

  if (action.payload.result) return;

  // yield loadNextScene();
}

function* loadNextScene(time = 1000) {
  const mode: Mode = yield select(selectSessionMode);
  if (mode === Mode.ADMIN) return;

  const action: IActionBasic = yield select(selectActionCurrent);
  const sceneId = yield select((state: RootState) => state.state[EntityTypes.SCENE]);
  const feed: IActionFeed = yield select(selectActionFeed);
  const nextItem: IActionFeedItem = feed.feed[feed.index + 1];

  const nextAction: IActionBasic = yield select((state: RootState) =>
    nextItem?.actionId ? selectAction(state, nextItem?.actionId) : null,
  );

  if (!action?.sceneId) {
    // Current slide has no scene of its own: point the renderer at the next scene
    // now so it's loaded and ready to display the moment we advance.
    if (nextAction?.sceneId && nextAction.sceneId !== sceneId) {
      yield put(yield call(loadingState, { scene: LoadingState.LOADING }));
      yield put(yield call(updateState, { type: EntityTypes.SCENE, id: nextAction.sceneId }));
    }
    return;
  }

  // Immediate next scene: prefetch its asset now (no pointer swap) so the very next
  // advance is instant and proceedSaga never stalls waiting for it.
  //
  // The one unsafe case is prefetching a VIDEO while the current slide is ALSO a video:
  // two live DASH players race on the shared teardown/seek ("Failed to set append
  // window" / "SourceBuffer has been removed"). Prefetching a video is fine when the
  // current slide has no active video (image or scene-less) — that's the fast
  // text→video / photo→video transition we want. Images/models never use DASH, so
  // they're always safe to prefetch.
  const currentIsVideo = action?.scene?.type === SceneType.VIDEO || !!action?.voiceSceneId;
  const nextIsVideo = nextAction?.scene?.type === SceneType.VIDEO;
  const unsafeVideoPrefetch = nextIsVideo && currentIsVideo;
  if (nextAction?.sceneId && nextAction.sceneId !== sceneId && !unsafeVideoPrefetch)
    yield loadSceneAsset(nextAction.sceneId);

  // Deeper look-ahead: only once the current video can play through uninterrupted,
  // so prefetching further scenes never steals bandwidth the current video needs.
  // (takeLatest cancels this whole saga if we navigate away before it finishes.)
  yield waitForCurrentVideoBuffered();
  yield prefetchAhead(feed, sceneId);
}

// Resolve once the current scene's video is comfortably buffered — i.e. the element
// reports HAVE_ENOUGH_DATA (can play to the end without stalling) and isn't actively
// buffering. Returns immediately if the current scene isn't a video. Capped so a
// stuck/errored stream can't block prefetch forever.
function* waitForCurrentVideoBuffered() {
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  if (!scene || scene.type !== SceneType.VIDEO) return;

  const videoEl = document.getElementById('videoEl') as HTMLVideoElement | null;
  if (!videoEl) return;

  const MAX_WAIT = 30000;
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT) {
    const buffering = yield select((state: RootState) => state.core.player.state.video.buffering);
    // HAVE_ENOUGH_DATA === 4
    if (!buffering && videoEl.readyState >= 4) return;
    yield delay(500);
  }
}

// Prefetch the assets for the next LOOKAHEAD scenes (skipping the immediate next,
// already prefetched above, and any scene id already loaded/seen).
//
// Only IMAGE scenes are deep-prefetched. Video/model scenes attach through the
// single shared #videoEl / DASH MediaSource; warming more than the immediate-next
// video leaves multiple live SourceBuffers that throw "SourceBuffer has been
// removed" when the current video's source is torn down on switch. Videos are left
// to the on-navigate attach flow (proceedSaga already gates the advance on ready).
function* prefetchAhead(feed: IActionFeed, currentSceneId: number) {
  const seen = new Set<number>([currentSceneId]);
  for (let i = 2; i <= LOOKAHEAD; i++) {
    const item: IActionFeedItem = feed.feed[feed.index + i];
    if (!item?.actionId) continue;
    const act: IActionBasic = yield select((state: RootState) => selectAction(state, item.actionId));
    if (!act?.sceneId || seen.has(act.sceneId)) continue;
    seen.add(act.sceneId);
    if (act.scene?.type !== SceneType.PHOTO) continue;
    yield loadSceneAsset(act.sceneId);
  }
}

function* watchRegisterAction() {
  yield takeEvery(registerAction, registerActionSaga);
}

function* watchViewerStateAction() {
  yield takeLatest(updateViewerState.type, viewerStateSaga);
}

export function* nextSceneSaga() {
  yield all([fork(watchViewerStateAction), fork(watchRegisterAction)]);
}
